import React, { useState, useEffect, useMemo } from "react";
import apiClient from "../api";
import { toast } from "react-hot-toast";
import { subDays, format } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";

// --- Reusable KPI Card Component (No changes) ---
const KpiCard = ({ title, value, formatAsCurrency = false }) => (
  <div className="bg-white rounded-lg shadow p-5">
    <h2 className="text-sm font-medium text-gray-500 mb-1">{title}</h2>
    <p className="text-3xl font-bold text-gray-900">
      {formatAsCurrency ? `Rs. ${Number(value || 0).toLocaleString()}` : value}
    </p>
  </div>
);

// --- helper: safe percent
const clampPercent = (n) => {
  const num = Number(n);
  if (Number.isNaN(num)) return 0;
  return Math.max(0, Math.min(100, num));
};

// --- Main Combined Component ---
const OperatorDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // This state now handles date ranges
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 6),
    to: new Date(),
  });

  // Effect to fetch all necessary data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get("/operator/dashboard", {
          // It sends startDate and endDate as required by the backend
          params: {
            startDate: format(dateRange.from, "yyyy-MM-dd"),
            endDate: format(dateRange.to, "yyyy-MM-dd"),
          },
        });
        setData(res.data);
      } catch (error) {
        toast.error(
          error.response?.data?.message || "Failed to load dashboard."
        );
        setData(null); // Set data to null on error to show "No data" message
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateRange]); // Refetches when the date range changes

  // Pre-defined date ranges for the buttons
  const dateRanges = {
    Today: { from: new Date(), to: new Date() },
    "Last 7 Days": { from: subDays(new Date(), 6), to: new Date() },
    "Last 30 Days": { from: subDays(new Date(), 29), to: new Date() },
  };

  // ✅ Fleet data (new backend) with safe fallbacks
  const todayTotals = data?.today?.totals || null;
  const todayTrips = Array.isArray(data?.todayTrips) ? data.todayTrips : [];
  const rangeDaily = Array.isArray(data?.rangeDaily) ? data.rangeDaily : [];

  // ✅ Determine which chart dataset to use
  // Prefer rangeDaily (net), fallback to old currentStats.dailyBreakdown (revenue)
  const chartData = useMemo(() => {
    if (rangeDaily.length > 0) {
      return rangeDaily.map((d) => ({
        date: d.date,
        revenue: Number(d.operatorNet || 0), // show NET as "Revenue" on chart
        gross: Number(d.grossRevenue || 0),
        bookings: Number(d.bookings || 0),
        seatsSold: Number(d.seatsSold || 0),
      }));
    }
    const old = data?.currentStats?.dailyBreakdown;
    if (Array.isArray(old) && old.length > 0) {
      return old.map((d) => ({
        date: d.date,
        revenue: Number(d.revenue || 0),
        bookings: Number(d.bookings || 0),
      }));
    }
    return [];
  }, [rangeDaily, data]);

  // ✅ quick computed stats for selected range (when rangeDaily exists)
  const rangeTotals = useMemo(() => {
    if (!rangeDaily.length) return null;
    return rangeDaily.reduce(
      (acc, d) => {
        acc.bookings += Number(d.bookings || 0);
        acc.seatsSold += Number(d.seatsSold || 0);
        acc.gross += Number(d.grossRevenue || 0);
        acc.net += Number(d.operatorNet || 0);
        return acc;
      },
      { bookings: 0, seatsSold: 0, gross: 0, net: 0 }
    );
  }, [rangeDaily]);

  // ✅ selected day label for Today section (uses startDate = dateRange.from)
  const selectedDayLabel = format(dateRange.from, "yyyy-MM-dd");

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-8 bg-gray-50 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Operator Portal</h1>
          <p className="text-sm text-gray-500 mt-1">
            Fleet overview for <span className="font-semibold">{selectedDayLabel}</span>
          </p>
        </div>

        {/* Date range buttons are always visible */}
        <div className="mt-4 sm:mt-0 flex items-center space-x-2 bg-white p-1 rounded-lg shadow-sm">
          {Object.keys(dateRanges).map((rangeName) => (
            <button
              key={rangeName}
              onClick={() => setDateRange(dateRanges[rangeName])}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors ${
                format(dateRange.from, "yyyy-MM-dd") ===
                  format(dateRanges[rangeName].from, "yyyy-MM-dd") &&
                format(dateRange.to, "yyyy-MM-dd") ===
                  format(dateRanges[rangeName].to, "yyyy-MM-dd")
                  ? "bg-blue-600 text-white"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              {rangeName}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {/* Dashboard Tab is always active on this page */}
          <button className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm border-blue-500 text-blue-600">
            Dashboard
          </button>
          {/* Profile Tab now navigates to the new profile page */}
          <button
            onClick={() => navigate("/operator/profile")}
            className="whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          >
            My Profile
          </button>
        </nav>
      </div>

      {/* Content Area */}
      <div>
        {loading ? (
          <p className="p-6 text-center">Loading...</p>
        ) : !data ? (
          <p className="p-6 text-center">No data available.</p>
        ) : (
          // Dashboard content is now always displayed
          <div className="space-y-8">
            {/* ✅ Fleet KPIs (Today) */}
            {todayTotals ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Today Overview ({data?.today?.date || selectedDayLabel})
                  </h3>
                  <button
                    onClick={() => navigate("/operator/buses")}
                    className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                  >
                    Manage Buses →
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <KpiCard title="Trips Today" value={todayTotals.trips || 0} />
                  <KpiCard title="Seats Sold Today" value={todayTotals.bookedSeats || 0} />
                  <KpiCard
                    title="Gross Today"
                    value={todayTotals.grossRevenue || 0}
                    formatAsCurrency
                  />
                  <KpiCard
                    title="Your Earnings Today (Net)"
                    value={todayTotals.operatorNet || 0}
                    formatAsCurrency
                  />
                </div>
              </div>
            ) : null}

            {/* ✅ Existing KPI row (keep) + improved labels when rangeDaily exists */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div
                className="bg-white rounded-lg shadow p-5 cursor-pointer hover:shadow-lg hover:scale-105 transition-transform duration-200"
                onClick={() => navigate("/operator/buses")}
              >
                <h2 className="text-sm font-medium text-gray-500 mb-1">
                  Total Buses
                </h2>
                <p className="text-3xl font-bold text-gray-900">
                  {data.totalBuses}
                </p>
              </div>

              <KpiCard
                title={rangeTotals ? "Gross (Selected Range)" : "Total Revenue"}
                value={rangeTotals ? rangeTotals.gross : data.currentStats.totalRevenue}
                formatAsCurrency
              />
              <KpiCard
                title={rangeTotals ? "Bookings (Selected Range)" : "Total Bookings"}
                value={rangeTotals ? rangeTotals.bookings : data.currentStats.totalBookings}
              />
            </div>

            {/* ✅ Today Trips Table (Fleet view) */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Today Trips (Seats per Trip)
                </h3>
                <button
                  onClick={() => navigate("/operator/buses")}
                  className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow hover:bg-blue-700 transition-colors"
                >
                  View & Manage My Buses <ArrowRight size={18} />
                </button>
              </div>

              {todayTrips.length === 0 ? (
                <p className="text-gray-500">
                  No trips found for the selected day. (Tip: choose “Today”)
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-3 pr-4">Departure</th>
                        <th className="py-3 pr-4">Bus</th>
                        <th className="py-3 pr-4">Route</th>
                        <th className="py-3 pr-4">Seats</th>
                        <th className="py-3 pr-4">Gross</th>
                        <th className="py-3 pr-4">Net</th>
                        <th className="py-3 pr-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {todayTrips.map((t, idx) => {
                        const cap = Number(t.capacity || 0);
                        const sold = Number(t.bookedSeats || 0);
                        const pct = cap > 0 ? clampPercent((sold / cap) * 100) : 0;

                        return (
                          <tr key={idx} className="border-b last:border-b-0">
                            <td className="py-4 pr-4 font-semibold text-gray-900 whitespace-nowrap">
                              {t.departureTime || "—"}
                            </td>
                            <td className="py-4 pr-4 text-gray-800 whitespace-nowrap">
                              {t.busName || "Untitled Bus"}
                            </td>
                            <td className="py-4 pr-4 text-gray-700 whitespace-nowrap">
                              {(t.from || "—")} → {(t.to || "—")}
                            </td>
                            <td className="py-4 pr-4">
                              <div className="min-w-[220px]">
                                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                  <span>
                                    {sold}/{cap || "—"} sold
                                  </span>
                                  <span>{pct.toFixed(0)}%</span>
                                </div>
                                <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-blue-600"
                                    style={{ width: `${pct}%` }}
                                  />
                                </div>
                              </div>
                            </td>
                            <td className="py-4 pr-4 font-semibold text-gray-900 whitespace-nowrap">
                              Rs. {Number(t.grossRevenue || 0).toLocaleString()}
                            </td>
                            <td className="py-4 pr-4 font-semibold text-gray-900 whitespace-nowrap">
                              Rs. {Number(t.operatorNet || 0).toLocaleString()}
                            </td>
                            <td className="py-4 pr-2 text-right">
                              <button
                                onClick={() => navigate(`/operator/bus/${t.busId}`)}
                                className="px-3 py-2 rounded-md bg-gray-900 text-white text-xs font-semibold hover:bg-black"
                              >
                                View Seats
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ✅ Revenue Overview (now NET if rangeDaily exists) */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-800">
                  Revenue Overview {rangeDaily.length > 0 ? "(Net)" : ""}
                </h3>
                {rangeDaily.length > 0 ? (
                  <p className="text-sm text-gray-500">
                    Showing net earnings (Gross − Convenience Fee)
                  </p>
                ) : null}
              </div>

              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(dateStr) =>
                      format(new Date(dateStr), "MMM d")
                    }
                  />
                  <YAxis />
                  <Tooltip
                    formatter={(value, name, props) => {
                      if (name === "Daily Revenue") {
                        return [`Rs. ${Number(value || 0).toLocaleString()}`, "Net"];
                      }
                      return [`${value}`, name];
                    }}
                  />
                  <Legend />
                  <Bar dataKey="revenue" fill="#3B82F6" name="Daily Revenue" />
                </BarChart>
              </ResponsiveContainer>

              {/* ✅ Daily table (fleet-friendly) */}
              {rangeDaily.length > 0 ? (
                <div className="mt-6 overflow-x-auto">
                  <h4 className="text-sm font-semibold text-gray-800 mb-3">
                    Daily Summary
                  </h4>
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 border-b">
                        <th className="py-2 pr-4">Date</th>
                        <th className="py-2 pr-4">Bookings</th>
                        <th className="py-2 pr-4">Seats Sold</th>
                        <th className="py-2 pr-4">Gross</th>
                        <th className="py-2 pr-4">Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rangeDaily.map((d, i) => (
                        <tr key={i} className="border-b last:border-b-0">
                          <td className="py-3 pr-4 font-medium text-gray-900 whitespace-nowrap">
                            {d.date}
                          </td>
                          <td className="py-3 pr-4 text-gray-800">
                            {Number(d.bookings || 0)}
                          </td>
                          <td className="py-3 pr-4 text-gray-800">
                            {Number(d.seatsSold || 0)}
                          </td>
                          <td className="py-3 pr-4 font-semibold text-gray-900 whitespace-nowrap">
                            Rs. {Number(d.grossRevenue || 0).toLocaleString()}
                          </td>
                          <td className="py-3 pr-4 font-semibold text-gray-900 whitespace-nowrap">
                            Rs. {Number(d.operatorNet || 0).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OperatorDashboard;
